import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Form, Spinner, ListGroup, Alert } from 'react-bootstrap';
import { fetchCategories, fetchArticles, fetchArticleById, clearCurrentArticle } from '../../redux/knowledgeBaseSlice';
import ArticleCard from './ArticleCard';
import ArticleDetailModal from './ArticleDetailModal';
import { toDisplayList, isActiveNode } from './categoryTree';

const BrowseTab = () => {
  const dispatch = useDispatch();
  const { categories, categoriesError, articles, currentArticle, loading, pagination } = useSelector(state => state.knowledgeBase);

  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);

  // Sidebar order: parents first, sub-categories indented beneath them
  // (WEB-830). A hidden parent takes its whole subtree with it; the API
  // already filters hidden categories for the public, this is a guard for
  // when the admin tab has loaded the full list into the same store.
  const categoryList = useMemo(() => toDisplayList(categories, isActiveNode), [categories]);

  const handleArticleClick = (article) => {
    dispatch(fetchArticleById(article.article_id));
    setShowModal(true);
  };

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchArticles({ categoryId: activeCategory, search, page }));
    }, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [dispatch, activeCategory, search, page]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
    setActiveCategory(null);
  };

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId === activeCategory ? null : categoryId);
    setSearch('');
    setPage(1);
  };

  return (
    <Row>
      {/* Sidebar */}
      <Col md={3}>
        <div className="sticky-top" style={{ top: '80px' }}>
          <div className="bg-white border rounded p-0 overflow-hidden">
            <div className="p-3 border-bottom">
              <small className="text-muted fw-bold text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.1em' }}>
                Categories
              </small>
            </div>
            {categoriesError && (
              <Alert variant="warning" className="m-2 py-2 mb-0" style={{ fontSize: '12px' }}>
                Categories could not be loaded. {categoriesError}
              </Alert>
            )}
            <ListGroup variant="flush" style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
              <ListGroup.Item
                action
                active={!activeCategory}
                onClick={() => handleCategoryClick(null)}
                style={{
                  fontSize: '13px',
                  borderLeft: !activeCategory ? '3px solid #e85d04' : '3px solid transparent',
                  background: !activeCategory ? '#fff3e0' : 'transparent',
                  // #a04020 on #fff3e0 is ~5.8:1 (passes AA). #bf4f00 measures ~4.4:1 (fails by a hair); the brand #e85d04 is ~3.95:1.
                  color: !activeCategory ? '#a04020' : '#444',
                  fontWeight: !activeCategory ? 700 : 400,
                }}
              >
                All Articles
              </ListGroup.Item>
              {categoryList.map(cat => {
                const selected = activeCategory === cat.category_id;
                return (
                  <ListGroup.Item
                    key={cat.category_id}
                    action
                    active={selected}
                    onClick={() => handleCategoryClick(cat.category_id)}
                    className="d-flex justify-content-between align-items-center"
                    title={cat.has_children ? 'Includes articles from its sub-categories' : undefined}
                    style={{
                      fontSize: '12px',
                      // Indent each level under its parent; 16px is the list's own padding.
                      paddingLeft: `${16 + cat.depth * 16}px`,
                      borderLeft: selected ? '3px solid #e85d04' : '3px solid transparent',
                      background: selected ? '#fff3e0' : 'transparent',
                      // #a04020 on #fff3e0 is ~5.8:1 (passes AA). #bf4f00 measures ~4.4:1 (fails by a hair); the brand #e85d04 is ~3.95:1.
                      color: selected ? '#a04020' : '#555',
                      fontWeight: selected ? 700 : cat.has_children ? 600 : 400,
                      lineHeight: 1.35,
                    }}
                  >
                    <span>
                      {/* Indentation is visual only; give screen readers the nesting too. */}
                      {cat.depth > 0 && (
                        <span className="visually-hidden">Sub-category, level {cat.depth + 1}: </span>
                      )}
                      {cat.icon} {cat.name}
                    </span>
                    <small className="text-muted">{cat.total_count}</small>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </div>
        </div>
      </Col>

      {/* Main content */}
      <Col md={9}>
        {/* Search bar */}
        <Form.Control
          type="text"
          placeholder="Search articles…"
          aria-label="Search knowledge base articles"
          value={search}
          onChange={handleSearch}
          className="mb-3"
          style={{ borderColor: '#e0e0e0' }}
        />

        {/* Results header */}
        {search && (
          <p className="text-muted mb-3" style={{ fontSize: '13px' }}>
            <strong className="text-dark">{pagination.total} result{pagination.total !== 1 ? 's' : ''}</strong> for "{search}"
          </p>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: '#e85d04' }} />
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-5">
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>🔍</div>
            <p className="text-muted">No articles found{search ? ` for "${search}"` : ''}.</p>
          </div>
        ) : (
          <>
            <Row xs={1} md={2} lg={3} className="g-3">
              {articles.map(article => (
                <Col key={article.article_id}>
                  <ArticleCard
                    article={article}
                    onClick={handleArticleClick}
                    searchQuery={search}
                  />
                </Col>
              ))}
            </Row>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-center gap-2 mt-4">
                {/* #5a6268 on white is ~5.5:1 — Bootstrap's default #6c757d is ~4.57:1 and WAVE flags it as just-below AA. */}
                <button
                  className="btn btn-outline-secondary btn-sm"
                  style={{ color: '#5a6268', borderColor: '#5a6268' }}
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </button>
                <span className="align-self-center" style={{ fontSize: '13px', color: '#5a6268' }}>
                  Page {page} of {pagination.totalPages}
                </span>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  style={{ color: '#5a6268', borderColor: '#5a6268' }}
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* Article detail modal */}
        <ArticleDetailModal
          article={currentArticle}
          show={showModal && !!currentArticle}
          onClose={() => { setShowModal(false); dispatch(clearCurrentArticle()); }}
        />
      </Col>
    </Row>
  );
};

export default BrowseTab;
